import React, { useEffect, useState } from "react";
import { useApp, AppScreen } from "../components/AppContext";
import { MainLayout } from "../components/MainLayout";
import { useTranslation } from "../i18n/TranslationContext";
import { useCurrentUser } from "../api";
import { useGetExtra } from "../api/extras/useGetExtra";
import { useGetExtraEvents } from "../api/extraEvents/useGetExtraEvents";

interface OnboardingStepProps {
	stepNumber: number;
}

interface OnboardingStep {
	step: number;
	id: string;
	title: string;
	route: AppScreen;
	completed: boolean;
	nextStep: number | null;
}

export function OnboardingStep({ stepNumber }: OnboardingStepProps) {
	const {
		getCurrentStepInfo,
		continueToNextStep,
		skipToNextStep,
		getOnboardingSequence,
		navigateDirectly,
		userCompletionState,
	} = useApp();
	const { t } = useTranslation();
	const { user } = useCurrentUser();
	const { extra, loading: profileLoading } = useGetExtra(user?.extraId);
	const { events, loading: eventsLoading } = useGetExtraEvents(
		user?.extraId || 0
	);
	const [dynamicSequence, setDynamicSequence] = useState<OnboardingStep[]>([]);

	// Build dynamic onboarding sequence based on what's completed
	useEffect(() => {
		if (!profileLoading && !eventsLoading && extra) {
			const sequence: OnboardingStep[] = [];
			let stepNum = 1;

			// Check if profile is complete
			const profileComplete = extra.profile_status === "ready_to_work";
			sequence.push({
				step: stepNum++,
				id: "profile",
				title: t("onboardingStep.profile.title"),
				route: "onboarding-step-profile",
				completed: profileComplete,
				nextStep: null,
			});

			// Check if code of conduct is accepted
			const codeOfConductAccepted = !!extra.code_of_conduct;
			sequence.push({
				step: stepNum++,
				id: "code-of-conduct",
				title: t("onboardingStep.codeOfConduct.title"),
				route: "onboarding-step-code-of-conduct",
				completed: codeOfConductAccepted,
				nextStep: null,
			});

			// Always include event application as LAST step
			// Check if user has any events (applied or booked)
			const eventApplicationCompleted = events && events.length > 0;
			sequence.push({
				step: stepNum++,
				id: "event-application",
				title: t("onboardingStep.eventApp.title"),
				route: "onboarding-step-event-application",
				completed: eventApplicationCompleted,
				nextStep: null,
			});

			// Update nextStep values
			sequence.forEach((step, index) => {
				step.nextStep =
					index < sequence.length - 1 ? sequence[index + 1].step : null;
			});

			setDynamicSequence(sequence);
		} else if (!profileLoading && !eventsLoading) {
			// Fallback to default sequence if no extra data
			const defaultSequence = getOnboardingSequence();
			// Convert default sequence to our typed format
			const typedSequence: OnboardingStep[] = defaultSequence.map((step) => ({
				...step,
				completed: false,
				nextStep: step.nextStep ?? null,
			}));
			setDynamicSequence(typedSequence);
		}
	}, [extra, profileLoading, eventsLoading, events, getOnboardingSequence, t]);

	// Use dynamic sequence if available, otherwise use default
	const sequenceToUse: OnboardingStep[] =
		dynamicSequence.length > 0
			? dynamicSequence
			: getOnboardingSequence().map((step) => ({
					...step,
					completed: false,
					nextStep: step.nextStep ?? null,
				}));

	const stepInfo = getCurrentStepInfo(stepNumber);
	const totalSteps = sequenceToUse.length;

	if (!stepInfo) {
		return <div>{t("onboardingStep.invalidStep")}</div>;
	}

	// Find the actual position of the current step in the sequence
	const currentStepPosition =
		sequenceToUse.findIndex((step) => step.id === stepInfo.id) + 1;

	// Calculate the number of completed steps for accurate progress
	const completedSteps = sequenceToUse.filter((step) => step.completed).length;

	// Get step-specific content and icon
	const getStepContent = () => {
		switch (stepInfo.id) {
			case "profile":
				return {
					icon: "👤",
					title: t("onboardingStep.profile.title"),
					description: t("onboardingStep.profile.description"),
					action: t("onboardingStep.profile.action"),
				};
			case "code-of-conduct":
				return {
					icon: "📋",
					title: t("onboardingStep.codeOfConduct.title"),
					description: t("onboardingStep.codeOfConduct.description"),
					action: t("onboardingStep.codeOfConduct.action"),
				};
			case "event-application":
				return {
					icon: "📅",
					title: t("onboardingStep.eventApp.title"),
					description: t("onboardingStep.eventApp.description"),
					action: t("onboardingStep.eventApp.action"),
				};
			default:
				return {
					icon: "❓",
					title: stepInfo.title,
					description: t("onboardingStep.unknownStep"),
					action: t("onboardingStep.continue"),
				};
		}
	};

	// Calculate progress based on current step position
	const progressPercentage =
		totalSteps > 0 ? (currentStepPosition / totalSteps) * 100 : 0;
	const stepContent = getStepContent();

	const handleContinue = () => {
		// Navigate to actual form for this step
		switch (stepInfo.id) {
			case "profile":
				navigateDirectly("intro-profile-completion");
				break;
			case "event-application":
				navigateDirectly("app-event-application");
				break;
			// COMMENTED OUT AS REQUESTED - Contract section
			// case 'contract-data':
			//   navigateDirectly('intro-contract-data');
			//   break;
			case "code-of-conduct":
				// Navigate to code of conduct intro, but we need to ensure back navigation works
				navigateDirectly("intro-code-of-conduct");
				break;
			// COMMENTED OUT AS REQUESTED - Payroll Information section
			// case 'payroll-info':
			//   navigateDirectly('intro-payroll-info');
			//   break;
			// COMMENTED OUT AS REQUESTED - ID Check section
			// case 'id-check':
			//   navigateDirectly('intro-id-check');
			//   break;
			default:
				continueToNextStep(stepNumber, { completed: true });
				break;
		}
	};

	const handleSkip = () => {
		skipToNextStep(stepNumber);
	};

	// Show loading state while fetching profile or events
	if (profileLoading || eventsLoading) {
		return (
			<MainLayout showBack={false} showBottomNav={false}>
				<div className="onboarding-step-container">
					<div className="flex flex-col items-center justify-center min-h-[60vh]">
						<div className="w-8 h-8 border-4 border-[var(--ucm-primary-teal)] border-t-transparent rounded-full animate-spin mb-4"></div>
						<p className="text-[var(--ucm-text-medium)]">
							{t("onboardingStep.loadingProgress")}
						</p>
					</div>
				</div>
			</MainLayout>
		);
	}

	return (
		<MainLayout showBack={false} showBottomNav={false}>
			<div className="onboarding-step-container">
				{/* Progress Header */}
				<div className="onboarding-header">
					<div className="step-progress">
						<span className="step-indicator">
							{t("onboardingStep.stepIndicator", {
								current: currentStepPosition,
								total: totalSteps,
							})}
						</span>
						<div className="progress-bar w-full bg-[var(--ucm-border-light)] rounded-full h-2 mt-2">
							<div
								className="progress-fill h-full bg-[var(--ucm-primary-teal)] rounded-full transition-all duration-300"
								style={{ width: `${progressPercentage}%` }}
							></div>
						</div>
					</div>
				</div>

				{/* Step Content */}
				<div className="onboarding-content">
					<div className="step-intro text-center mb-6">
						<h2 className="text-2xl font-bold text-[var(--ucm-text-dark)] mb-2">
							{stepContent.title}
						</h2>
						<p className="text-[var(--ucm-text-medium)]">
							{t("onboardingStep.completeSetup")}
						</p>
					</div>

					{/* Step-specific content */}
					<div
						className="step-form-content bg-white rounded-2xl p-6 mx-4 shadow-[0_4px_16px_rgba(0,0,0,0.08)]"
					>
						<div className="text-center mb-6">
							<div className="w-20 h-20 bg-[var(--ucm-primary-teal)] rounded-full flex items-center justify-center mx-auto mb-4">
								<span className="text-3xl">{stepContent.icon}</span>
							</div>
							<h3 className="text-lg font-semibold text-[var(--ucm-text-dark)] mb-3">
								{stepContent.title}
							</h3>
							<p className="text-sm text-[var(--ucm-text-medium)] leading-relaxed">
								{stepContent.description}
							</p>
						</div>

						<div className="bg-[var(--ucm-light-blue)] border border-[var(--ucm-info-blue)] rounded-xl p-4">
							<div className="flex items-start space-x-3">
								<span className="text-lg">💡</span>
								<div>
									<h4 className="font-semibold text-[var(--ucm-dark-blue)] mb-1 text-base">
										{t("onboardingStep.whatHappensNext")}
									</h4>
									<p className="text-sm text-[var(--ucm-dark-blue)] leading-relaxed">
										{t("onboardingStep.whatHappensNextDescription")}
									</p>
								</div>
							</div>
						</div>
					</div>

					{/* Navigation Actions - moved inside content area */}
					<div className="onboarding-actions mt-8 px-4 pb-8">
						<button
							className="secondary-button skip-button h-14 bg-[var(--ucm-disabled-gray)] text-[var(--ucm-text-dark)] font-semibold rounded-full hover:bg-[var(--ucm-border-medium)] transition-colors duration-200 mr-3 flex items-center justify-center text-center"
							onClick={handleSkip}
						>
							{t("onboardingStep.skipForNow")}
						</button>

						<button
							className="primary-button continue-button h-14 bg-[var(--ucm-primary-teal)] text-white font-semibold rounded-full hover:bg-[var(--ucm-primary-teal-hover)] transition-colors duration-200 flex items-center justify-center text-center"
							onClick={handleContinue}
						>
							{stepContent.action}
						</button>
					</div>
				</div>
			</div>
		</MainLayout>
	);
}
